import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { taskService, jobService, scheduleService, taskScheduleService } from '../services';
import {
  formatData,
  formatDuration,
  handleError,
  Role,
  TaskType,
  cronDescription,
  getTimezoneAbbreviation,
} from '../helpers';
import {
  Button,
  DataTable,
  Icon,
  MainColumn,
  MorphButton,
  ObjectDetail,
  ObjectDetails,
  Tabs,
} from '../components';
import {
  useInterval,
  useSideDialog,
  useModalDialog,
  useGlobalState,
  useNotFound,
  useMessage,
  usePhotos,
} from '../hooks';

export const Task = () => {
  const [task, setTask] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [schedulesAll, setSchedulesAll] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingSchedulesTable, setLoadingSchedulesTable] = useState(true);

  const clearSelectedSchedules = () => setSelectedSchedules([]);

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPhotos } = usePhotos();
  const { notFound, setNotFound, NotFound } = useNotFound();

  const { task_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = loggedInUser && loggedInUser.user_role_name === Role.admin;

  // Update job steps and schedule assignments continuously
  useInterval(() => {
    if (isAdmin) {
      fetchItems();
      fetchJobs();
      fetchSchedules();
    }
  }, 3000);

  useEffect(() => {
    fetchItems();
    fetchJobs(true);
    isAdmin && fetchSchedules();
    isAdmin && fetchAllSchedules();
  }, []);

  const fetchItems = async () => {
    taskService
      .getById(task_id)
      .then(res => {
        if (!res) return;

        if (res?.tasks && res.tasks.length < 1) {
          setNotFound(true);
        } else if (res?.tasks) {
          const tasks_tmp = res.tasks.map(task => {
            return {
              ...task,
              connector_name_tooltip:
                task.connector_code === 'odbc'
                  ? `${task.connector_name} (${task.connector_driver_name})`
                  : task.connector_name,
            };
          });

          setTask(tasks_tmp[0]);
          setLoading(false);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  const fetchJobs = async (fetchPhoto = false) => {
    jobService
      .getByTaskId(task_id)
      .then(res => {
        if (!res) return;
        let maxRuntimeValue = 0;
        let maxChangedAssetValue = 0;

        res.jobs.forEach(row => {
          if (row.job_runtime_seconds > maxRuntimeValue) {
            maxRuntimeValue = row.job_runtime_seconds;
          }
          if (row.changed_asset_count > maxChangedAssetValue) {
            maxChangedAssetValue = row.changed_asset_count;
          }
        });

        const jobs_tmp = res.jobs.map(job => {
          return {
            ...job,
            job_runtime_seconds_max_value: maxRuntimeValue,
            job_runtime_tooltip: formatDuration(job.job_runtime_seconds) + ' (hh:mm:ss)',
            changed_asset_count_max_value: maxChangedAssetValue,
            job_started_timestamp: job.job_started_timestamp || job.job_enqueued_timestamp,
          };
        });

        setJobs(prev => {
          return jobs_tmp.map(job => ({
            ...prev.find(x => x.job_id === job.job_id),
            ...job,
            job_executed_by_link:
              job.job_execution_method === 'manual' && job.user_id
                ? `/users/${job.user_id}`
                : job.schedule_id
                  ? `/schedules/${job.schedule_id}`
                  : undefined,
          }));
        });
        setLoadingTable(false);
        fetchPhoto && isAdmin && fetchPhotos(jobs_tmp, 'user_id', setJobs);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Fetches all schedules this task is assigned to and enriches them with display fields.
   */
  const fetchSchedules = async () => {
    scheduleService
      .getByTaskId(task_id)
      .then(res => {
        if (!res) return;
        const enriched = res.schedules.map(s => {
          const desc = cronDescription(s.schedule_cron);
          const abbr = getTimezoneAbbreviation(s.schedule_timezone || 'UTC');
          return {
            ...s,
            schedule_cron_description: desc ? `${desc} (${abbr})` : desc,
            schedule_next_run_timestamp: s.schedule_is_enabled
              ? formatData(s.schedule_next_run_timestamp, 'datetime')
              : 'Disabled',
            schedule_next_run_className: s.schedule_is_enabled ? '' : 'small-text',
          };
        });
        setSchedules(enriched);
        setLoadingSchedulesTable(false);
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {
          setLoadingSchedulesTable(false);
        });
      });
  };

  /**
   * Fetches all schedules to populate the MorphButton assignment dropdown.
   */
  const fetchAllSchedules = async () => {
    scheduleService
      .getAll()
      .then(res => {
        if (!res) return;
        setSchedulesAll(res.schedules);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Submits schedule edits via the API and refreshes the schedules tab.
   * @param {Object|Object[]} data - Schedule data from the edit form.
   */
  const editSchedule = async data => {
    const payload = { schedules: Array.isArray(data) ? data : [data] };

    await scheduleService
      .update(payload)
      .then(res => {
        fetchSchedules();
        showMessage(res.message);
        clearSelectedSchedules();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Toggle handler for the MorphButton. Assigns this task to the schedule if not
   * already assigned, or removes it if it is.
   * @param {{ value: number }} event - Select change event with the selected schedule_id.
   */
  const updateSchedule = async event => {
    const { value } = event;
    const existingSchedule = schedules.find(s => s.schedule_id === value);
    if (existingSchedule) {
      await removeFromTask([existingSchedule]);
    } else {
      await assignToTask(value);
    }
  };

  /**
   * Assigns this task to a schedule via the task_schedule junction endpoint.
   * @param {number} scheduleId - The schedule to assign.
   */
  const assignToTask = async scheduleId => {
    await taskScheduleService
      .create({ task_schedules: [{ task_id: parseInt(task_id), schedule_id: scheduleId }] })
      .then(res => {
        fetchSchedules();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Removes this task from one or more schedules via the task_schedule junction endpoint.
   * Does NOT delete the schedule entity.
   * @param {Object[]} data - Schedule objects to remove this task from.
   */
  const removeFromTask = async data => {
    await taskScheduleService
      .destroy({
        task_schedules: data.map(s => ({ task_id: parseInt(task_id), schedule_id: s.schedule_id })),
      })
      .then(res => {
        hideModalDialog();
        clearSelectedSchedules();
        fetchSchedules();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const updateJobStatus = (task_arr, job) => {
    setTask(prev => ({
      ...prev,
      job_status: job.job_status,
      job_completion_message: job.job_completion_message,
    }));

    // Add dummy job
    /*setJobs([
      {
        task_id: job.task_id, 
        job_id: job.job_id, 
        job_status: job.job_status, 
        job_started_timestamp: job.job_started_timestamp, 
        job_execution_method: job.job_execution_method,
        job_executed_by_user: loggedInUser.user_fullname,
        user_id: loggedInUser.user_id
      },
      ...jobs
    ])*/
  };

  const editTask = async data => {
    const tasks = { tasks: Array.isArray(data) ? data : [data] };

    await taskService
      .update(tasks)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Enqueues this task for execution.
   * Optimistically marks the task as 'queued' in the UI before the request completes.
   */
  const runTask = async () => {
    updateJobStatus([task], {
      task_id: task.task_id,
      job_status: 'queued',
      job_completion_message: 'Queued at ' + new Date().toLocaleString('sv-SE'),
    });

    await taskService
      .runMany([task.task_id])
      .then(res => {
        res && showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        fetchItems();
        fetchJobs();
      });
  };

  /**
   * Aborts the current task — dequeues it if queued, or sends an abort signal if running.
   */
  const abortTask = async () => {
    await taskService
      .abortMany([task.task_id])
      .then(res => res && showMessage(res.message))
      .catch(err => showMessage(err, 'error'))
      .finally(() => {
        fetchItems();
        fetchJobs();
      });
  };

  const deleteTask = async data => {
    await taskService
      .destroy({ tasks: data })
      .then(async res => {
        hideModalDialog();
        showMessage(res.message);

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/tasks`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleShowMessage = data => {
    showModalDialog('showMessage', data);
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  const data = loading ? [] : task;

  const scheduleIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  const scheduleOptions = schedulesAll.map(s => ({
    value: s.schedule_id,
    label: s.schedule_name,
    tooltip: s.schedule_description,
  }));

  const isRunning = data.job_status === 'running';
  const isQueued = data.job_queue_position != null;
  const isActive = isRunning || isQueued;

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Task"
          title={data.task_name}
          subtitleText={data && !loading && data.task_type_name + ' Task'}
          details={[
            { title: 'Last Modified', data: formatData(data.modified_timestamp, 'datetime') },
            { title: 'Created', data: formatData(data.created_timestamp, 'datetime') },
            { title: 'Id', data: data.task_id, tooltip: 'Katalogue-internal id for this asset' },
            ...(isAdmin
              ? [
                  {
                    title: 'Consecutive Failures',
                    data: data.consecutive_failure_count ?? 0,
                    tooltip:
                      'Consecutive scheduler-triggered failures since last successful run.\nThe task will be auto-disabled if the configured threshold is reached.\nResets to 0 on any successful completion.',
                  },
                ]
              : []),
          ]}
          description={data.task_description}
          loading={loading}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editTask', [data], editTask),
            },
            {
              value: 'Run',
              disabled: isActive || !data.task_is_enabled,
              tooltip: isActive
                ? 'Task is already queued or running'
                : !data.task_is_enabled
                  ? 'Task is disabled'
                  : undefined,
              onClick: runTask,
            },
            isActive && {
              value: 'Cancel',
              tooltip: 'Cancel this task',
              onClick: abortTask,
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteTask', [data], deleteTask),
            },
          ]}
        >
          {data.task_type_code === TaskType.dssync && (
            <ObjectDetail label="Connection" loading={loading} showLoadingForChildren={true}>
              {data.connection_name === null ? (
                '-'
              ) : isAdmin ? (
                <Link to={'/connections/' + data.connection_id} className="link">
                  <Icon name={data.connector_code} tooltip={data.connector_name_tooltip} />
                  {data.connection_name}
                </Link>
              ) : (
                <React.Fragment>
                  <Icon name={data.connector_code} tooltip={data.connector_name_tooltip} />
                  {data.connection_name}
                </React.Fragment>
              )}
            </ObjectDetail>
          )}

          {isAdmin && (
            <>
              <ObjectDetail label="Priority" loading={loading} showLoadingForChildren={true}>
                {data.task_priority}
              </ObjectDetail>

              <ObjectDetail label="Status" loading={loading} showLoadingForChildren={true}>
                {!data.task_is_enabled ? (
                  <span
                    className="pill pill-semifull-red"
                    title={
                      data.consecutive_failure_count > 0
                        ? `Auto-disabled after ${data.consecutive_failure_count} consecutive scheduled failure${data.consecutive_failure_count === 1 ? '' : 's'}`
                        : undefined
                    }
                  >
                    Disabled
                  </span>
                ) : (
                  <span className="pill pill-enabled">Enabled</span>
                )}
              </ObjectDetail>
            </>
          )}
          <ObjectDetail
            label="Next Run"
            loading={loading}
            showLoadingForChildren={true}
            tooltip={data.next_schedule_name || undefined}
          >
            {!data.next_run_timestamp ? (
              <span className="small-text">No schedule</span>
            ) : !data.task_is_enabled ? (
              <span className="small-text">Disabled</span>
            ) : (
              formatData(data.next_run_timestamp, 'datetime')
            )}
          </ObjectDetail>

          <ObjectDetail label="Last Run Completed" loading={loading} showLoadingForChildren={true}>
            {data.job_completed_timestamp === null ? (
              <span className="small-text">Never run</span>
            ) : (
              formatData(data.job_completed_timestamp, 'datetime')
            )}
          </ObjectDetail>
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs className="slim left">
          <div label="Jobs" tabid="jobs">
            {jobs.length > 0 || loadingTable ? (
              <DataTable
                columns={[
                  { id: 'job_status', type: 'icon', tooltip: 'job_completion_message' },
                  {
                    id: 'job_started_timestamp',
                    name: 'Started',
                    link: '/tasks/:task_id/:job_id',
                    type: 'datetime',
                    className: 'bold',
                  },
                  { id: 'job_completed_timestamp', name: 'Completed', type: 'datetime' },
                  isAdmin && {
                    id: 'job_runtime_seconds',
                    name: 'Runtime [s]',
                    type: 'chart',
                    chartType: 'bar',
                    data: { data: 'job_runtime_seconds', max: 'job_runtime_seconds_max_value' },
                    tooltip: 'job_runtime_tooltip',
                  },
                  ['adsync', 'dssync'].includes(task.task_type_code) && {
                    id: 'changed_asset_count',
                    name: 'Changed Assets',
                    type: 'chart',
                    chartType: 'bar',
                    data: { data: 'changed_asset_count', max: 'changed_asset_count_max_value' },
                    tooltip: 'changed_asset_count',
                  },
                  isAdmin && {
                    id: 'job_execution_method',
                    name: 'Execution Method',
                    className: 'small-text',
                  },
                  isAdmin && {
                    id: 'job_executed_by_user',
                    name: 'Executed By',
                    link: ':job_executed_by_link',
                    type: 'owner',
                    data: { photo: 'user_photo' },
                  },
                  isAdmin && {
                    id: 'job_completion_message',
                    name: 'Message',
                    type: 'button',
                    label: 'Show',
                    onClick: props => handleShowMessage(props),
                    data: { data: 'job_completion_message' },
                  },
                ]}
                data={jobs}
                idColumn="job_id"
                loading={loadingTable}
                editable={false}
                filterable={true}
                filterObjects={[
                  'job_started_timestamp',
                  'job_completed_timestamp',
                  ...(isAdmin
                    ? [
                        'job_status',
                        'job_execution_method',
                        'job_executed_by_user',
                        'job_completion_message',
                      ]
                    : []),
                ]}
              />
            ) : (
              !loadingTable && <div className="no-result-text">No jobs found for this task</div>
            )}
          </div>

          {isAdmin && (
            <div
              label="Schedules"
              tabid="schedules"
              resultCount={
                loadingSchedulesTable || schedules.length === 0 ? undefined : schedules.length
              }
            >
              <DataTable
                columns={[
                  {
                    id: 'schedule_name',
                    name: 'Name',
                    link: '/schedules/:schedule_id',
                    tooltip: 'schedule_description',
                    className: 'bold',
                  },
                  { id: 'schedule_cron_description', name: 'Schedule', className: 'small-text' },
                  {
                    id: 'schedule_cron',
                    name: 'Cron',
                    type: 'code',
                    tooltip: 'schedule_cron_description',
                  },
                  {
                    id: 'schedule_next_run_timestamp',
                    name: 'Next Run',
                    className: ':schedule_next_run_className',
                  },
                  {
                    id: 'schedule_is_enabled',
                    name: 'Status',
                    type: 'boolean',
                    values: scheduleIsEnabledValues,
                  },
                ]}
                buttons={[
                  {
                    customButton: (
                      <MorphButton
                        name="schedule_id"
                        value={schedules.map(s => ({
                          value: s.schedule_id,
                          label: s.schedule_name,
                        }))}
                        options={scheduleOptions}
                        onChange={updateSchedule}
                        placeholder="Search Schedules to add..."
                        onToggle={clearSelectedSchedules}
                      />
                    ),
                    action: 'add',
                    onClick: () => {},
                  },
                  {
                    label: 'Edit',
                    action: 'edit',
                    onClick: items =>
                      showSideDialog('editSchedule', items || selectedSchedules, editSchedule),
                  },
                  {
                    label: 'Remove',
                    action: 'remove',
                    tooltip:
                      'Remove task from this schedule. The schedule itself will not be deleted.',
                    onClick: items =>
                      showModalDialog(
                        'removeScheduleFromTask',
                        items || selectedSchedules,
                        removeFromTask
                      ),
                  },
                ]}
                data={schedules}
                idColumn="schedule_id"
                selectedItems={selectedSchedules}
                setSelectedItems={setSelectedSchedules}
                loading={loadingSchedulesTable}
                editable={editable}
                filterable={true}
                filterObjects={['schedule_name', 'schedule_description', 'schedule_is_enabled']}
                filterObjectsValueMap={{ schedule_is_enabled: scheduleIsEnabledValues }}
              />
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
};
